import { Component, lazy, Suspense, type ReactNode } from 'react'
import { Alert, Spin } from 'antd'
import { Navigate, Route, Routes } from 'react-router-dom'
import PublicLayout from './layouts/PublicLayout'
import AdminLayout from './layouts/AdminLayout'
import Home from './pages/Home'
import Search from './pages/Search'
import SearchDocPreview from './pages/SearchDocPreview'
import Chat from './pages/Chat'
import KnowledgeLibrary from './pages/KnowledgeLibrary'
import EnterpriseKnowledge from './pages/EnterpriseKnowledge'
import KnowledgeCreateDoc from './pages/KnowledgeCreateDoc'
import DocLocateHome from './pages/DocLocateHome'
import DocLocateWorkspace from './pages/DocLocateWorkspace'
import DocLocateLegacyRedirect from './pages/DocLocateLegacyRedirect'
import Login from './pages/Login'
import AdminLogin from './pages/manage/Login'
import AdminIndexRedirect from './pages/manage/AdminIndexRedirect'
import SmsSettings from './pages/manage/SmsSettings'
import ChatQaEval from './pages/manage/ChatQaEval'
import Users from './pages/manage/Users'
import OrgStructure from './pages/manage/OrgStructure'
import OrgSync from './pages/manage/OrgSync'
import Roles from './pages/manage/Roles'
import Menus from './pages/manage/Menus'
import ClassificationManage from './pages/manage/ClassificationManage'
import ClassificationRuleDetail from './pages/manage/ClassificationRuleDetail'
import IngestionManage from './pages/manage/IngestionManage'
import Settings from './pages/manage/Settings'
import CleaningSettings from './pages/manage/CleaningSettings'
import DedupSettings from './pages/manage/DedupSettings'
import DesensitizeSettings from './pages/manage/DesensitizeSettings'
import GraphManage from './pages/manage/GraphManage'
import QaSettings from './pages/manage/QaSettings'
import ModelSettings from './pages/manage/ModelSettings'
import McpSettings from './pages/manage/McpSettings'
import StorageSettings from './pages/manage/StorageSettings'
import Neo4jSettings from './pages/manage/Neo4jSettings'
import OcrSettings from './pages/manage/OcrSettings'
import SearchDict from './pages/manage/SearchDict'
import AttributeExtract from './pages/manage/AttributeExtract'
import DocLocateSettings from './pages/manage/DocLocateSettings'
import DatasetManage from './pages/manage/DatasetManage'
import OpenApiManage from './pages/manage/OpenApiManage'
import DatasetSyncJobs from './pages/manage/DatasetSyncJobs'
import PipelineJobs from './pages/manage/PipelineJobs'
import DocTemplateManage from './pages/manage/DocTemplateManage'
import AgentSettings from './pages/manage/AgentSettings'
import FileDedup from './pages/FileDedup'
import DedupSourceMatches from './pages/DedupSourceMatches'
import DocCompare from './pages/DocCompare'
import DocCompareResult from './pages/DocCompareResult'
import DocDesensitize from './pages/DocDesensitize'
const KnowledgeGraph = lazy(() => import('./pages/KnowledgeGraph'))

class GraphBoundary extends Component<{ children: ReactNode }, { err: Error | null }> {
  state = { err: null as Error | null }
  static getDerivedStateFromError(e: Error) {
    return { err: e }
  }
  override render() {
    if (this.state.err) {
      return <Alert type="error" title="知识图谱加载失败" description={this.state.err.message} />
    }
    return this.props.children
  }
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/manage/login" element={<AdminLogin />} />
      <Route element={<PublicLayout />}>
        <Route path="/" element={<Home />} />
        <Route path="/search" element={<Search />} />
        <Route path="/chat" element={<Chat />} />
        <Route path="/knowledge" element={<KnowledgeLibrary />} />
        <Route path="/enterprise" element={<EnterpriseKnowledge />} />
        <Route path="/locate" element={<DocLocateHome />} />
        <Route path="/locate/:itemId" element={<DocLocateWorkspace />} />
        <Route path="/enterprise/bid-parse/:itemId" element={<DocLocateLegacyRedirect />} />
        <Route path="/graph"
          element={
            <GraphBoundary>
              <Suspense fallback={<Spin style={{ margin: 48 }} />}>
                <KnowledgeGraph />
              </Suspense>
            </GraphBoundary>
          }
        />
        <Route path="/dedup" element={<FileDedup />} />
        <Route path="/compare" element={<DocCompare />} />
        <Route path="/desensitize" element={<DocDesensitize />} />
      </Route>
      <Route path="/dedup/matches/:reportId/:itemId" element={<DedupSourceMatches />} />
      <Route path="/compare/result/:sessionId" element={<DocCompareResult />} />
      <Route path="/search/preview/:itemId" element={<SearchDocPreview />} />
      <Route path="/knowledge/create" element={<KnowledgeCreateDoc />} />
      <Route path="/enterprise/create" element={<KnowledgeCreateDoc />} />
      <Route path="/manage" element={<AdminLayout />}>
        <Route index element={<AdminIndexRedirect />} />
        <Route path="users" element={<Users />} />
        <Route path="org-structure" element={<OrgStructure />} />
        <Route path="org-sync" element={<OrgSync />} />
        <Route path="roles" element={<Roles />} />
        <Route path="menus" element={<Menus />} />
        <Route path="settings" element={<Settings />} />
        <Route path="classification" element={<ClassificationManage />} />
        <Route path="classification/:ruleId" element={<ClassificationRuleDetail />} />
        <Route path="ingestion" element={<IngestionManage />} />
        <Route path="doc-categories" element={<Navigate to="/manage/classification" replace />} />
        <Route path="cleaning-settings" element={<CleaningSettings />} />
        <Route path="dedup-settings" element={<DedupSettings />} />
        <Route path="desensitize-settings" element={<DesensitizeSettings />} />
        <Route path="doc-templates" element={<DocTemplateManage />} />
        <Route path="quality" element={<Navigate to="/manage/cleaning-settings" replace />} />
        <Route path="graph" element={<GraphManage />} />
        <Route path="graph-extract" element={<Navigate to="/manage/graph" replace />} />
        <Route path="graph-review" element={<Navigate to="/manage/graph" replace />} />
        <Route path="models/*" element={<ModelSettings />} />
        <Route path="content-extract" element={<Navigate to="/manage/settings" replace />} />
        <Route path="qa-settings" element={<QaSettings />} />
        <Route path="mcp" element={<McpSettings />} />
        <Route path="storage" element={<StorageSettings />} />
        <Route path="neo4j" element={<Neo4jSettings />} />
        <Route path="ocr" element={<OcrSettings />} />
        <Route path="sms" element={<SmsSettings />} />
        <Route path="search-dict" element={<SearchDict />} />
        <Route path="attribute-extract" element={<AttributeExtract />} />
        <Route path="doc-locate/*" element={<DocLocateSettings />} />
        <Route path="agents/*" element={<AgentSettings />} />
        <Route path="datasets" element={<DatasetManage />} />
        <Route path="open-api" element={<OpenApiManage />} />
        <Route path="open-api/logs" element={<OpenApiManage />} />
        <Route path="dataset-sync" element={<DatasetSyncJobs />} />
        <Route path="pipeline-jobs" element={<PipelineJobs />} />
        <Route path="chat-qa" element={<ChatQaEval />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}
